import { useEffect, useState, type FormEvent } from "react";
import { api, errorMessage, isCode } from "../api/client.ts";
import type { TelegramData } from "../api/types.ts";
import { useAsync } from "../lib/hooks.ts";
import { CodeInput } from "./CodeInput.tsx";
import { IconExternal } from "./Icons.tsx";
import { StepUpDialog } from "./StepUp.tsx";
import { useToast } from "./Toast.tsx";
import { Alert, Spinner, StatusPill } from "./ui.tsx";
import { fmt, rich, useT } from "../i18n/index.tsx";

export const useTelegram = () => useAsync(() => api.get<TelegramData>("/telegram"), []);

/** Security page: link this login to a Telegram account to approve payouts there (Owners only). */
export function TelegramCard() {
  const t = useT();
  const q = useTelegram();
  const toast = useToast();
  const [id, setId] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [unlink, setUnlink] = useState(false);
  const tg = q.data;
  const status = tg?.link?.status;

  // While the bot waits for "Confirm link" in Telegram, check every few seconds.
  const setData = q.setData;
  useEffect(() => {
    if (status !== "pending") return;
    const timer = setInterval(() => {
      api.get<TelegramData>("/telegram").then((d) => setData(d)).catch(() => {});
    }, 4000);
    return () => clearInterval(timer);
  }, [status, setData]);

  const link = async (e: FormEvent) => {
    e.preventDefault();
    if (!/^\d{4,20}$/.test(id.trim())) return setError(t.settings.tgUserIdInvalid);
    if (code.length !== 6) return setCodeError(t.settings.codeRequired);
    setBusy(true);
    setError(null);
    setCodeError(null);
    try {
      await api.put("/telegram/link", { telegram_user_id: id.trim(), code });
      setCode("");
      await q.reload();
    } catch (err) {
      setCode("");
      if (isCode(err, "mfa_required")) setCodeError(t.settings.codeFailed);
      else setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card section-block" aria-labelledby="tg-h">
      <div className="card-head">
        <div>
          <h2 id="tg-h" className="card-title">{t.settings.tgTitle}</h2>
          <p className="card-sub">{t.settings.tgSub}</p>
        </div>
        {status === "linked" && <StatusPill status="active" label={t.settings.tgLinked} />}
        {status === "pending" && <StatusPill status="pending" label={t.settings.tgWaiting} />}
      </div>

      {q.error ? (
        <Alert tone="error">{errorMessage(q.error)}</Alert>
      ) : !tg ? (
        <span className="sk sk-lg" />
      ) : !tg.available ? (
        <p className="muted small">{t.settings.tgUnavailable}</p>
      ) : !tg.is_owner ? (
        <p className="muted">{t.settings.tgOwnersOnly}</p>
      ) : status === "linked" && tg.link ? (
        <div className="tg-linked">
          <p>
            {rich(t.settings.tgLinkedTo, { code: (c) => <span className="muted mono-sm">{c}</span> }, {
              who: tg.link.username ? `@${tg.link.username}` : t.settings.tgYourAccount,
              id: tg.link.telegram_user_id,
            })}
            {" "}{tg.organization.enabled ? t.settings.tgLinkedOn : t.settings.tgLinkedOrgOff}
          </p>
          <p className="muted small">{t.settings.tgKeepSafe}</p>
          <button type="button" className="btn btn-ghost btn-danger-ghost" onClick={() => setUnlink(true)}>{t.settings.tgUnlinkBtn}</button>
        </div>
      ) : status === "pending" && tg.link ? (
        <div className="tg-pending">
          <Spinner />
          <div>
            <strong>{t.settings.tgGoBack}</strong>
            <p className="muted small">{fmt(t.settings.tgSentTo, { id: tg.link.telegram_user_id })}</p>
          </div>
          <button type="button" className="btn btn-sm btn-ghost" onClick={() => setUnlink(true)}>{t.common.cancel}</button>
        </div>
      ) : (
        <form className="form-stack" onSubmit={link} noValidate>
          {status === "error" && tg.link?.error && <Alert tone="error" title={t.settings.tgLinkFailed}>{tg.link.error}</Alert>}
          <ol className="tg-steps">
            <li>
              <span>{rich(t.settings.tgStep1, {}, { bot: tg.bot_username })}</span>
              <a className="btn btn-sm btn-ghost" href={tg.bot_url} target="_blank" rel="noreferrer noopener">{t.settings.tgOpenBot} <IconExternal size={14} /></a>
            </li>
            <li>
              <span>{t.settings.tgStep2}</span>
            </li>
            <li>
              <span>{rich(t.settings.tgStep3)}</span>
            </li>
          </ol>
          <div className="field tg-id">
            <label htmlFor="tg-id">{t.settings.tgUserId}</label>
            <input id="tg-id" inputMode="numeric" autoComplete="off" value={id} onChange={(e) => setId(e.target.value.replace(/[^\d]/g, ""))} placeholder={t.settings.tgUserIdPlaceholder} />
          </div>
          <CodeInput value={code} onChange={(v) => { setCode(v); setCodeError(null); }} error={codeError} disabled={busy} />
          {error && <Alert tone="error">{error}</Alert>}
          <div>
            <button type="submit" className="btn btn-primary" disabled={busy}>{busy && <Spinner />} {t.settings.tgLink}</button>
          </div>
        </form>
      )}

      <StepUpDialog
        open={unlink}
        onClose={() => setUnlink(false)}
        title={t.settings.tgUnlinkTitle}
        description={t.settings.tgUnlinkDesc}
        confirmLabel={t.settings.tgUnlink}
        danger
        onConfirm={async (c) => {
          await api.del("/telegram/link", { code: c });
          toast(t.settings.tgUnlinked);
          void q.reload();
        }}
      />
    </section>
  );
}

/** Settings page (Owners): switch Telegram approvals on for the organization, with an optional USD cap. */
export function TelegramSetting() {
  const t = useT();
  const q = useTelegram();
  const toast = useToast();
  const [enabled, setEnabled] = useState(false);
  const [max, setMax] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);
  const tg = q.data;

  useEffect(() => {
    if (!tg) return;
    setEnabled(tg.organization.enabled);
    setMax(tg.organization.max_usd ?? "");
  }, [tg]);

  if (!tg || !tg.is_owner || !tg.available) return null;
  const dirty = enabled !== tg.organization.enabled || (max.trim() || null) !== (tg.organization.max_usd ?? null);

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (max.trim() && !/^\d+(\.\d{1,2})?$/.test(max.trim())) return setError(t.settings.tgCapInvalid);
    if (code.length !== 6) return setCodeError(t.settings.codeRequired);
    setBusy(true);
    setError(null);
    setCodeError(null);
    try {
      const r = await api.put<{ enabled: boolean; max_usd: string | null }>("/settings/telegram-approval", { enabled, max_usd: max.trim() || null, code });
      q.setData((d) => (d ? { ...d, organization: r } : d));
      setCode("");
      toast(r.enabled ? t.settings.tgOn : t.settings.tgOff);
    } catch (err) {
      setCode("");
      if (isCode(err, "mfa_required")) setCodeError(t.settings.codeFailed);
      else setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card section-block" aria-labelledby="tgs-h">
      <h2 id="tgs-h" className="card-title">{t.settings.tgOrgTitle}</h2>
      <p className="card-sub">{fmt(t.settings.tgOrgSub, { bot: tg.bot_username })}</p>
      <form className="form-stack tg-form" onSubmit={save} noValidate>
        <label className="switch-row">
          <input type="checkbox" role="switch" className="switch" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          <span>
            <strong>{t.settings.tgSendRequests}</strong>
            <small>{enabled ? t.settings.on : t.settings.off}</small>
          </span>
        </label>
        {enabled && (
          <div className="field tg-cap">
            <label htmlFor="tg-max">{t.settings.tgCap} <span className="optional">{t.common.optional}</span></label>
            <div className="input-affix">
              <input id="tg-max" inputMode="decimal" value={max} onChange={(e) => setMax(e.target.value)} placeholder={t.settings.tgNoLimit} />
              <span className="affix">USD</span>
            </div>
            <p className="field-hint">{t.settings.tgCapHint}</p>
          </div>
        )}
        <ul className="tg-rules">
          <li>{t.settings.tgRule1}</li>
          <li>{t.settings.tgRule2}</li>
          <li>{t.settings.tgRule3}</li>
        </ul>
        {dirty && <CodeInput value={code} onChange={(v) => { setCode(v); setCodeError(null); }} error={codeError} disabled={busy} />}
        {error && <Alert tone="error">{error}</Alert>}
        <div>
          <button type="submit" className="btn btn-primary" disabled={busy || !dirty}>{busy && <Spinner />} {t.common.save}</button>
        </div>
      </form>
    </section>
  );
}
